import React, { useState, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { H5 } from '../../../../AbstractElements';
import { PlusCircle, Edit, File, Trash2 } from 'react-feather';
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import PaymentCollectionDataModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionDataModal';
import { confirmDelete } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';

const renderCellWithTooltip = (text, maxWidth = '150px') => (
    <div
        title={text}
        style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: maxWidth,
        }}
    >
        {text}
    </div>
);

const useTableState = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearRows, setClearRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedItem, setSelectedItem] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedItem(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedItem(null);
        setModalOpen(true);
    };

    const openModifyModal = (item) => {
        setModalMode('modify');
        setSelectedItem(item);
        setModalOpen(true);
    };

    return {
        selectedRows,
        clearRows,
        setClearRows,
        deleteModal,
        setDeleteModal,
        modalOpen,
        setModalOpen,
        modalMode,
        selectedItem,
        page,
        setPage,
        pageSize,
        setPageSize,
        totalRows,
        setTotalRows,
        handleRowSelected,
        toggleModal,
        openAddModal,
        openModifyModal,
        setSelectedRows,
    };
};

const DataSection = ({ title, buttonText, columns, data, tableState, onDelete, children }) => (
    <>
        <div className="d-flex justify-content-between align-items-center mb-3">
            <H5>{title}</H5>
            <div className="d-flex">
                <div className="btn btn-outline-primary ms-2 d-flex align-items-center" onClick={tableState.openAddModal}>
                    <PlusCircle className="me-1" />
                    {buttonText}
                </div>
                {(tableState.selectedRows.length !== 0) &&
                    <div className='btn btn-outline-danger ms-2 d-flex align-items-center' onClick={onDelete}>
                        <Trash2 size={18} className="me-1"/>
                        Seçilenleri Sil
                    </div>
                }
            </div>
        </div>

        {data.length === 0 ? (
            <p>Müşteriye ait {title.toLowerCase()} bulunamadı.</p>
        ) : (
            <DataTable
                columns={columns}
                data={data}
                onSelectedRowsChange={tableState.handleRowSelected}
                pagination
                paginationServer
                selectableRows
                clearSelectedRows={tableState.clearRows}
                paginationTotalRows={tableState.totalRows}
                onChangePage={page => tableState.setPage(page)}
                onChangeRowsPerPage={pageSize => tableState.setPageSize(pageSize)}
            />
        )}
        {children}
    </>
);

const ExpenseAndCollectionInfo = ({ expenses = [], paymentCollections = [], onDataChange, customerId }) => {
    const expenseState = useTableState();
    const paymentState = useTableState();
    const navigate = useNavigate();

    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';
    const paymentCollectionApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/paymentcollection';

    const handleCallForm = (fileId) => {
        navigate(`/callform/${fileId}`);
    };

    const createActionsColumn = (onEditClick) => ({
        name: 'Düzenle',
        cell: (row) => (
            <div
                className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                onClick={() => onEditClick(row)}
                style={{ padding: '0.3rem 0.6rem' }}
                title="Düzenle"
            >
                <Edit size={18} />
            </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        right: true,
    });

    const expenseColumns = [
        { name: 'Masraf Numarası', selector: row => row.expenseNumber || '', sortable: true },
        { name: 'Masraf Türü', selector: row => row.expenseType || '', sortable: true },
        { name: 'Tutar', selector: row => row.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(row.amount) : '', sortable: true },
        { name: 'Tarih ve Saat', selector: row => row.date ? new Date(row.date).toLocaleString() : '', sortable: true },
        // { name: 'Ödeme Durumu', selector: row => row.paymentStatus || '', sortable: true },
        { name: 'Fatura Numarası', selector: row => row.invoiceNumber || '', sortable: true },
        { name: 'Açıklama', selector: row => row.description || '', sortable: true },
        { 
            name: 'Tahsilat Numarası', 
            selector: row => {
                if (Array.isArray(row.paymentCollection) && row.paymentCollection.length > 0) {
                    return row.paymentCollection[0].paymentCollectionNumber || `ID: ${row.paymentCollection[0].id}`;
                }
                if (row.paymentCollection && typeof row.paymentCollection === 'object' && !Array.isArray(row.paymentCollection)) {
                    return row.paymentCollection.paymentCollectionNumber || `ID: ${row.paymentCollection.id}`;
                }
                return '';
            },
            cell: row => {
                let displayText = '';
                const pcs = row.paymentCollection;
                if (Array.isArray(pcs) && pcs.length > 0) {
                    const collectionTexts = pcs.map(pc => pc.paymentCollectionNumber || `ID: ${pc.id}`);
                    const totalCount = collectionTexts.length;
                    if (totalCount <= 3) {
                        displayText = collectionTexts.join(', ');
                    } else {
                        const firstThree = collectionTexts.slice(0, 3).join(', ');
                        const remainingCount = totalCount - 3;
                        displayText = `${firstThree} ... +${remainingCount}`;
                    }
                } else if (pcs && typeof pcs === 'object' && !Array.isArray(pcs)) {
                    displayText = pcs.paymentCollectionNumber || `ID: ${pcs.id}`;
                }
                return renderCellWithTooltip(displayText, '200px');
            },
            sortable: true,
            width: '220px'
        },
        {
            name: 'Çağrı Formu',
            cell: row => {
                const callFormId = row.callForm?.[0];
                return callFormId ? (
                    <div className="btn btn-outline-info d-flex align-items-center justify-content-center" onClick={() => handleCallForm(callFormId)}>
                        <File size={18} />
                    </div>
                ) : '';
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        },
        createActionsColumn(expenseState.openModifyModal)
    ];

    const paymentColumns = [
        { name: 'Tahsilat Numarası', selector: row => row.paymentCollectionNumber || '', sortable: true },
        { name: 'Tahsilat Tipi', selector: row => row.paymentCollectionType || '', sortable: true },
        { name: 'Tutar', selector: row => row.amount || '', sortable: true },
        { name: 'Tarih ve Saat', selector: row => row.date ? new Date(row.date).toLocaleString() : '', sortable: true },
        { name: 'Ödeme Yöntemi', selector: row => row.paymentMethod || '', sortable: true },
        { name: 'Makbuz Numarası', selector: row => row.receiptNumber || '', sortable: true },
        { name: 'Açıklama', selector: row => row.description || '', sortable: true },
        { 
            name: 'Masraf Numarası', 
            selector: row => row.expense?.expenseNumber || '', 
            sortable: true 
        },
        {
            name: 'Çağrı Formu',
            cell: row => {
                const callFormId = row.callForm?.[0];
                return callFormId ? (
                    <div className="btn btn-outline-info d-flex align-items-center justify-content-center" onClick={() => handleCallForm(callFormId)}>
                        <File size={18} />
                    </div>
                ) : '';
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
        createActionsColumn(paymentState.openModifyModal)
    ];

    const validateBeforeDelete = (items, type) => {
        // Add validation logic based on type (expense or payment)
        if (type === 'expense') {
            // Validate expenses
        } else {
            // Validate payments
        }
        return true;
    };

    return (
        <>
            <DataSection
                title="Masraflar"
                buttonText="Masraf Ekle"
                columns={expenseColumns}
                data={expenses}
                tableState={expenseState}
                onDelete={() => {
                    if (validateBeforeDelete(expenseState.selectedRows, 'expense')) {
                        expenseState.setDeleteModal(true);
                    } else {
                        toast.error('Seçili masraflar silinemez.');
                    }
                }}
            >
                <ExpenseDataModal
                    apiEndpoint={expenseApiUrl}
                    isOpen={expenseState.modalOpen}
                    toggle={expenseState.toggleModal}
                    mode={expenseState.modalMode}
                    selectedExpense={expenseState.selectedItem}
                    refreshData={onDataChange}
                    customerId={customerId}
                    autoAssignCustomer={true}
                    accidentFile={[]}
                />

                <DeleteConfirmationModal
                    isOpen={expenseState.deleteModal}
                    toggle={() => expenseState.setDeleteModal(!expenseState.deleteModal)}
                    itemsToDelete={expenseState.selectedRows}
                    displayKey="expenseNumber"
                    onConfirm={() =>
                        confirmDelete({
                            apiEndpoint: expenseApiUrl,
                            selectedItems: expenseState.selectedRows,
                            toggleModalCallback: () => expenseState.setDeleteModal(false),
                            clearSelectionCallback: () => {
                                expenseState.setSelectedRows([]);
                                expenseState.setClearRows(!expenseState.clearRows);
                            },
                            refreshDataCallback: onDataChange,
                        })
                    }
                />
            </DataSection>

            <div style={{ height: "20px" }}></div>
            <hr className="mt-4 mb-4" />
            <div style={{ height: "20px" }}></div>

            <DataSection
                title="Tahsilatlar"
                buttonText="Tahsilat Ekle"
                columns={paymentColumns}
                data={paymentCollections}
                tableState={paymentState}
                onDelete={() => paymentState.setDeleteModal(true)}
            >
                <PaymentCollectionDataModal
                    apiEndpoint={paymentCollectionApiUrl}
                    isOpen={paymentState.modalOpen}
                    toggle={paymentState.toggleModal}
                    mode={paymentState.modalMode}
                    selectedPaymentCollection={paymentState.selectedItem}
                    refreshData={onDataChange}
                    customerId={customerId}
                    autoAssignCustomer={true}
                />

                <DeleteConfirmationModal
                    isOpen={paymentState.deleteModal}
                    toggle={() => paymentState.setDeleteModal(!paymentState.deleteModal)}
                    itemsToDelete={paymentState.selectedRows}
                    displayKey="paymentCollectionNumber"
                    onConfirm={() =>
                        confirmDelete({
                            apiEndpoint: paymentCollectionApiUrl,
                            selectedItems: paymentState.selectedRows,
                            toggleModalCallback: () => paymentState.setDeleteModal(false),
                            clearSelectionCallback: () => {
                                paymentState.setSelectedRows([]);
                                paymentState.setClearRows(!paymentState.clearRows);
                            },
                            refreshDataCallback: onDataChange,
                        })
                    }
                />
            </DataSection>
        </>
    );
};

export default ExpenseAndCollectionInfo;
